import type { ForwardedRef, ReactNode, ComponentPropsWithoutRef, RefObject, JSX } from 'react';
import { forwardRef } from 'react';
import { styled } from 'styled-components';
import {
  Root as DialogRoot,
  Portal as DialogPortal,
  Description as DialogDescription,
} from '@radix-ui/react-dialog';
import { isNotNil } from '@wistia/type-guards';
import { visuallyHiddenStyle } from '../../css/visuallyHiddenStyle';
import { ModalHeader } from './ModalHeader';
import { ModalContent } from './ModalContent';
import { ModalOverlay } from './ModalOverlay';
import { DEFAULT_MODAL_WIDTH } from './constants';

const ModalHiddenDescription = styled(DialogDescription)({ ...visuallyHiddenStyle });

const ModalBody = styled.div`
  flex-direction: column;
  display: flex;
  flex: 1 0 0;
  padding: 0 var(--wui-space-05);
`;

const ModalScrollArea = styled.div`
  order: 2;
  max-height: 90vh;
  overflow-y: auto;
  height: 100%;
`;

const ModalFooter = styled.footer`
  padding: 0 var(--wui-space-05);
  order: 3;
`;

export type ModalProps = ComponentPropsWithoutRef<'div'> & {
  /**
   * The content of the modal.
   */
  children: ReactNode;
  /**
   * Content that should be static at the bottom of the modal here (e.g. action buttons).
   */
  footer?: ReactNode;
  /**
   * The variant of the modal position
   */
  positionVariant?: 'centered' | 'fixed-top' | 'right-side-panel';
  /**
   * Ref to the element that should receive focus when the modal opens
   */
  initialFocusRef?: RefObject<HTMLElement | null>;
  /**
   * Determines if the modal is currently open
   */
  isOpen: boolean;
  /**
   * Determines if the close button is visible (if false, `esc` will still close the Modal)
   */
  hideCloseButton?: boolean;
  /**
   * Determines if the title is visible
   */
  hideTitle?: boolean;
  /**
   * When the Modal is dismissed, this function will be called. You'll typically want to set the
   * state of `isOpen` to false here
   */
  onRequestClose?: () => void;
  /**
   * The title for the modal. If you want to hide the title, set the `hideTitle` prop to `true`
   * and the title will be hidden but still accessible to screen readers.
   */
  title: string;
  /**
   * The width of the modal when space allows. On smaller screens, the modal automatically adjusts to fit the available space.
   */
  width?: string;
};

/**
 * A Modal is a focused UI element that appears atop the main interface, requiring
 * user interaction or dismissal before returning to the underlying content.
 */
export const Modal = forwardRef<HTMLDivElement, ModalProps>(
  (
    {
      children,
      footer,
      hideCloseButton = false,
      hideTitle = false,
      initialFocusRef,
      isOpen,
      onRequestClose,
      positionVariant = 'centered',
      title,
      width = DEFAULT_MODAL_WIDTH,
      ...props
    },
    ref: ForwardedRef<HTMLDivElement>,
  ): JSX.Element => {
    return (
      <DialogRoot
        onOpenChange={(open) => {
          if (!open && isNotNil(onRequestClose)) {
            onRequestClose();
          }
        }}
        open={isOpen}
      >
        <DialogPortal>
          <ModalOverlay />
          <ModalContent
            ref={ref}
            onOpenAutoFocus={(event) => {
              // this callback fires right before the Dialog tries to focus the first focusable element
              // if `initialFocusRef` is provided, override the default focusing behavior
              if (isNotNil(initialFocusRef) && initialFocusRef.current) {
                event.preventDefault();
                requestAnimationFrame(() => {
                  initialFocusRef.current?.focus();
                });
              }
            }}
            positionVariant={positionVariant}
            width={width}
            {...props}
          >
            <ModalScrollArea>
              <ModalBody>{children}</ModalBody>
            </ModalScrollArea>
            {isNotNil(footer) ? <ModalFooter>{footer}</ModalFooter> : null}
            {/* hide entire header if both child elements are hidden to remove any space in DOM */}
            {hideCloseButton && hideTitle ? null : (
              <ModalHeader
                hideCloseButton={hideCloseButton}
                hideTitle={hideTitle}
                title={title}
              />
            )}
            {/* see: https://www.radix-ui.com/primitives/docs/components/dialog#description */}
            <ModalHiddenDescription />
          </ModalContent>
        </DialogPortal>
      </DialogRoot>
    );
  },
);

Modal.displayName = 'Modal';
